import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-react'
import type { FpdConfig, FpdDay, FpdOption, Scope } from '../../core/types'
import { useAuth } from '../../core/auth'
import { holds, SCOPE_LABELS } from '../../core/caps'
import { Loading, Empty, ErrorState } from '../../ui/States'
import { Text } from '../../ui/Field'
import { MyRequests, useWaitingCount } from './Requests'
import { CollaborateTeaser } from './Collaborate'
import { useMayGiveFpdRoles } from './FpdStaff'
import {
  buildingsFor, useFpdYear, useFpdDays, useFpdConfig, useMyRequests, saveYear, addDay, updateDay, removeDay, saveConfig, fpdScopes, type DayInput,
} from './model'
import {
  schoolYearOf, nextYearOf, daysFor, upcoming, isoToday, monthGrid, buildingsLabel, thresholdProblem, BUILDING_SCOPES, totals,
} from './logic'

/**
 * Flex PD, phase 1: the year's requirement and the flex day calendar for
 * everyone, and Setup for the district office (hours per year, the flex days,
 * the option lists). Requests, approvals and the tracker come next and hang
 * off these.
 */

const fmt = (iso: string, o: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }) =>
  new Date(iso + 'T12:00:00').toLocaleDateString(undefined, o)

/** Setup is the district office's, and only when held district-wide (the rules agree). */
function useMaySetUp() {
  const { profile } = useAuth()
  return !!profile && (!!profile.districtAdmin || holds(profile.caps, 'flexpd.configure', 'district'))
}

/** Under Flex PD in the rail. */
export function FlexPdNav() {
  const { pathname } = useLocation()
  const { profile } = useAuth()
  const maySetUp = useMaySetUp()
  const mayGive = useMayGiveFpdRoles()
  const waiting = useWaitingCount()
  const deciding = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.approve').length > 0
  const seeing = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.approve', 'flexpd.view').length > 0
  const links = [
    { to: '/flex-pd', label: 'My Flex PD' },
    { to: '/flex-pd/collaborate', label: 'Find collaborators' },
    seeing && { to: '/flex-pd/review', label: deciding ? 'Requests to review' : 'All requests', n: deciding ? waiting : undefined },
    seeing && { to: '/flex-pd/tracker', label: 'Hours tracker' },
    maySetUp && { to: '/flex-pd/setup', label: 'Setup: hours, flex days, lists' },
    mayGive && { to: '/flex-pd/staff', label: 'Flex PD staff and roles' },
  ].filter(Boolean) as Array<{ to: string; label: string; n?: number }>
  if (!pathname.startsWith('/flex-pd') && links.length < 2) return null
  return (
    <div className="rail__chk" aria-label="Flex PD">
      <ul>
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className={`rail__queue${pathname === l.to ? ' is-here' : ''}`}
              aria-current={pathname === l.to ? 'page' : undefined}><span>{l.label}</span>
              {l.n ? <span className="work__badge" aria-label={`${l.n} waiting on you`}>{l.n}</span> : null}</Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ---------------- the Flex PD home ---------------- */

export function FlexPd() {
  const { profile } = useAuth()
  const sy = schoolYearOf()
  const year = useFpdYear(sy.id)
  const allDays = useFpdDays(sy.id)
  const maySetUp = useMaySetUp()
  const scopes = fpdScopes(profile?.caps, profile?.districtAdmin)
  const days = useMemo(() => daysFor((allDays ?? []).filter((d) => !d.closed), scopes), [allDays, scopes])
  if (year === undefined || allDays === null) return <Loading label="Loading Flex PD" />

  const soon = upcoming(days)
  const next = soon[0]
  const daysTo = next ? Math.round((new Date(next.date + 'T12:00:00').getTime() - new Date(isoToday() + 'T12:00:00').getTime()) / 86_400_000) : null

  return (
    <>
      <section className="banner" aria-label="Flex PD">
        <div className="banner__main">
          <h1 className="banner__eyebrow">Flex PD</h1>
          <p className="banner__dates">{year?.label ?? sy.label} school year</p>
          <ul className="banner__dl">
            {year?.totalHours != null
              ? <li><strong>{year.totalHours} hours</strong> required this year{year.collabHours != null && <>, <strong>{year.collabHours}</strong> of them collaborative</>}</li>
              : <li>This year&apos;s required hours are not set yet</li>}
          </ul>
        </div>
        {next && daysTo !== null && (
          <div className="banner__count"><b>{daysTo === 0 ? 'Today' : daysTo}</b><span>{daysTo === 0 ? 'is a flex day' : daysTo === 1 ? 'day to the next flex day' : 'days to the next flex day'}</span></div>
        )}
      </section>

      <ReviewCard />
      <MyProgress yearId={sy.id} total={year?.totalHours ?? null} collab={year?.collabHours ?? null} />
      <MyRequests yearId={sy.id} />
      <CollaborateTeaser />

      <h2 className="work__heading">Upcoming flex days</h2>
      {soon.length === 0 ? (
        <Empty title="No flex days on the calendar yet" body={maySetUp ? 'Add this year’s dates under Setup.' : 'The district office adds them; check back soon.'} />
      ) : (
        <ul className="fpd__days">
          {soon.slice(0, 8).map((d) => <DayCard key={d.id} d={d} />)}
        </ul>
      )}

      <h2 className="work__heading cal__heading">The year at a glance</h2>
      <MonthCalendar days={days} start={year?.start ?? sy.start} end={year?.end ?? sy.end} />
    </>
  )
}

/** For principals: what waits on them, the page's first action. Coaches and the district get a way in. */
function ReviewCard() {
  const { profile } = useAuth()
  const waiting = useWaitingCount()
  const deciding = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.approve').length > 0
  const seeing = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.approve', 'flexpd.view').length > 0
  if (!seeing) return null
  return (
    <section className="nextup" aria-label="Requests">
      <div className="nextup__main">
        <div className="nextup__eyebrow">{deciding ? 'Waiting on you' : 'Your buildings'}</div>
        <h2 className="nextup__title"><Link to="/flex-pd/review">{deciding
          ? waiting === 0 ? 'Nothing to approve or verify right now' : `${waiting} ${waiting === 1 ? 'request' : 'requests'} to approve or verify`
          : 'Every Flex PD request, read only'}</Link></h2>
        <p className="nextup__detail"><Link to="/flex-pd/tracker">See every teacher’s hours this year →</Link></p>
      </div>
      <Link to="/flex-pd/review" className="btn nextup__go" tabIndex={-1} aria-hidden="true">{deciding ? 'Review' : 'Open'}</Link>
    </section>
  )
}

/** A teacher's own hours this year: banked (verified) against the requirement, and what is planned. */
function MyProgress({ yearId, total, collab }: { yearId: string; total: number | null; collab: number | null }) {
  const { profile, user } = useAuth()
  const mayRequest = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.request').length > 0
  const rows = useMyRequests(yearId, (user?.email ?? '').toLowerCase(), mayRequest)
  if (!mayRequest || !rows) return null
  const t = totals(rows)
  const bar = (have: number, need: number | null) => (need ? Math.min(100, (have / need) * 100) : 0)
  return (
    <section className="fpd__progress" aria-label="Your hours this year">
      <div className="fpd__stat">
        <div className="fpd__k">Hours verified</div>
        <div className="fpd__v">{fmtH(t.banked)}{total != null && <span> of {total}</span>}</div>
        {total != null && <div className="meter" aria-hidden="true"><span style={{ width: `${bar(t.banked, total)}%` }} /></div>}
      </div>
      <div className="fpd__stat">
        <div className="fpd__k">Collaborative</div>
        <div className="fpd__v">{fmtH(t.collabBanked)}{collab != null && <span> of {collab}</span>}</div>
        {collab != null && <div className="meter" aria-hidden="true"><span style={{ width: `${bar(t.collabBanked, collab)}%` }} /></div>}
      </div>
      <div className="fpd__stat">
        <div className="fpd__k">Planned</div>
        <div className="fpd__v">{fmtH(t.planned)}</div>
        <div className="work__sub">asked for or done, not yet verified</div>
      </div>
    </section>
  )
}
const fmtH = (h: number) => String(Math.round(h * 100) / 100)

function DayCard({ d }: { d: FpdDay }) {
  return (
    <li className="fpd__day card">
      <div className="fpd__date">
        <span className="fpd__dow">{fmt(d.date, { weekday: 'short' })}</span>
        <span className="fpd__dnum">{fmt(d.date, { day: 'numeric' })}</span>
        <span className="fpd__mon">{fmt(d.date, { month: 'short' })}</span>
      </div>
      <div className="fpd__dmain">
        <div style={{ fontWeight: 650 }}>{fmt(d.date, { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        <div className="work__sub">{buildingsLabel(d.buildings, SCOPE_LABELS)}{d.cap ? ` · up to ${d.cap} people` : ''}</div>
        {d.note && <p className="fpd__note">{d.note}</p>}
      </div>
    </li>
  )
}

/** A plain month grid, flex days shaded and labelled; a desk view, so phones get the list above instead. */
function MonthCalendar({ days, start, end }: { days: FpdDay[]; start: string; end: string }) {
  const today = isoToday()
  const clamp = today < start ? start : today > end ? end : today
  const [ym, setYm] = useState(() => ({ y: Number(clamp.slice(0, 4)), m: Number(clamp.slice(5, 7)) - 1 }))
  const byDate = useMemo(() => new Map(days.map((d) => [d.date, d])), [days])
  const weeks = monthGrid(ym.y, ym.m)
  const firstMonth = { y: Number(start.slice(0, 4)), m: Number(start.slice(5, 7)) - 1 }
  const lastMonth = { y: Number(end.slice(0, 4)), m: Number(end.slice(5, 7)) - 1 }
  const atStart = ym.y === firstMonth.y && ym.m === firstMonth.m
  const atEnd = ym.y === lastMonth.y && ym.m === lastMonth.m
  const step = (by: number) => setYm(({ y, m }) => { const d = new Date(y, m + by, 1); return { y: d.getFullYear(), m: d.getMonth() } })
  const flexThisMonth = days.filter((d) => d.date.startsWith(`${ym.y}-${String(ym.m + 1).padStart(2, '0')}`)).length
  return (
    <section className="card cal" aria-label="Flex day calendar">
      <div className="cal__head">
        <button className="opt__icon" onClick={() => step(-1)} disabled={atStart} aria-label="Previous month"><ChevronLeft size={18} aria-hidden="true" /></button>
        <h3 className="cal__title">{new Date(ym.y, ym.m, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</h3>
        <button className="opt__icon" onClick={() => step(1)} disabled={atEnd} aria-label="Next month"><ChevronRight size={18} aria-hidden="true" /></button>
        <span className="work__sub" style={{ marginLeft: 'auto' }}>{flexThisMonth === 0 ? 'No flex days' : flexThisMonth === 1 ? '1 flex day' : `${flexThisMonth} flex days`}</span>
      </div>
      <table className="cal__grid">
        <thead><tr>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <th key={d} scope="col">{d}</th>)}</tr></thead>
        <tbody>
          {weeks.map((w, i) => (
            <tr key={i}>
              {w.map((c) => {
                const f = byDate.get(c.iso)
                return (
                  <td key={c.iso} className={`cal__cell${c.inMonth ? '' : ' is-out'}${f ? ' is-flex' : ''}${c.iso === today ? ' is-today' : ''}`}>
                    <span className="cal__n">{c.day}</span>
                    {f && c.inMonth && <span className="cal__tag">Flex day{f.note ? <span className="cal__tagnote">{f.note}</span> : null}</span>}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

/* ---------------- setup: the district office ---------------- */

export function FlexPdSetup() {
  const maySetUp = useMaySetUp()
  const thisYear = schoolYearOf()
  const [yearId, setYearId] = useState(thisYear.id)
  const years = [thisYear, nextYearOf(thisYear.id)]
  if (!maySetUp) {
    return <Empty title="Setup is the district office’s" body="Required hours, flex days and the option lists are set by whoever holds Flex PD District office for the whole district." />
  }
  const y = years.find((x) => x.id === yearId)!
  return (
    <>
      <Link to="/flex-pd" className="backlink">← Flex PD</Link>
      <h1 className="page-title">Flex PD setup</h1>
      <p className="page-sub">Each school year has its own required hours and flex days. Nothing carries over from one year to the next.</p>
      <p className="page-sub">Who has which Flex PD role: <Link to="/flex-pd/staff">Flex PD staff and roles →</Link></p>
      <div className="chips" role="tablist" aria-label="School year" style={{ margin: 'var(--s-4) 0 0' }}>
        {years.map((x) => (
          <button key={x.id} role="tab" aria-selected={x.id === yearId} className={`chip${x.id === yearId ? ' is-on' : ''}`} onClick={() => setYearId(x.id)}>
            {x.label}{x.id === thisYear.id ? ' (this year)' : ' (next year)'}
          </button>
        ))}
      </div>
      <YearHours key={`h${y.id}`} base={y} />
      <DaysEditor key={`d${y.id}`} yearId={y.id} start={y.start} end={y.end} />
      <OptionLists />
    </>
  )
}

function YearHours({ base }: { base: ReturnType<typeof schoolYearOf> }) {
  const { profile } = useAuth()
  const year = useFpdYear(base.id)
  const [total, setTotal] = useState<string | null>(null)
  const [collab, setCollab] = useState<string | null>(null)
  const [start, setStart] = useState<string | null>(null)
  const [end, setEnd] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  if (year === undefined) return <Loading label="Loading the year" />
  const num = (v: string) => (v.trim() === '' ? null : Number(v))
  const t = total ?? (year?.totalHours ?? '').toString()
  const c = collab ?? (year?.collabHours ?? '').toString()
  const s = start ?? year?.start ?? base.start
  const e = end ?? year?.end ?? base.end
  const problem = thresholdProblem(num(t), num(c)) ?? (s >= e ? 'The year has to end after it starts.' : null)
  const dirty = total !== null || collab !== null || start !== null || end !== null
  const save = async () => {
    setBusy(true); setErr(null); setSaved(false)
    try {
      await saveYear({ id: base.id, label: base.label, start: s, end: e, totalHours: num(t), collabHours: num(c) }, profile?.displayName ?? '')
      setTotal(null); setCollab(null); setStart(null); setEnd(null); setSaved(true)
    } catch (x) { setErr(x instanceof Error ? x.message : String(x)) } finally { setBusy(false) }
  }
  return (
    <section className="card" style={{ marginTop: 'var(--s-4)' }}>
      <h2 className="ov__h">Required hours, {base.label}</h2>
      <p className="ov__p">What every certified teacher needs this year. Change it any time; teachers&apos; &ldquo;still needed&rdquo; follows at once.</p>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      <div className="host__grid">
        <Text label="Total hours" type="number" min={0} value={t} onChange={setTotal} placeholder="e.g. 12" />
        <Text label="Of those, collaborative hours" type="number" min={0} value={c} onChange={setCollab} placeholder="e.g. 6" />
        <Text label="Year starts" type="date" value={s} onChange={setStart} />
        <Text label="Year ends" type="date" value={e} onChange={setEnd} />
      </div>
      {problem && <p className="opt__hint" role="alert" style={{ color: 'var(--accent-dark)' }}>{problem}</p>}
      <div className="opt__bar" style={{ marginTop: 'var(--s-2)', alignItems: 'center' }}>
        <button className="btn" style={{ width: 'auto' }} disabled={busy || !!problem || (!dirty && !!year)} onClick={save}>
          {busy ? 'Saving…' : year ? 'Save changes' : `Set up ${base.label}`}
        </button>
        {saved && !dirty && <span className="work__sub" role="status">Saved.</span>}
        {year?.updatedBy && !dirty && !saved && <span className="work__sub">Last changed by {year.updatedBy}</span>}
      </div>
    </section>
  )
}

const BLANK_DAY: DayInput = { date: '', buildings: ['district'], cap: null, note: '', closed: false }

function DaysEditor({ yearId, start, end }: { yearId: string; start: string; end: string }) {
  const { profile } = useAuth()
  const days = useFpdDays(yearId)
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [err, setErr] = useState<string | null>(null)
  if (days === null) return <Loading label="Loading flex days" />
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date))
  const who = profile?.displayName ?? ''
  const run = (fn: () => Promise<unknown>) => async () => {
    setErr(null)
    try { await fn(); setEditing(null) } catch (x) { setErr(x instanceof Error ? x.message : String(x)) }
  }
  return (
    <section className="card" style={{ marginTop: 'var(--s-4)' }}>
      <h2 className="ov__h">Flex days</h2>
      <p className="ov__p">The dates teachers may use, for every building or only some. A cap limits how many people can request a date.</p>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      {sorted.length === 0 && editing !== 'new' && <p className="muted-note">No flex days for this year yet.</p>}
      <ul className="work" style={{ marginTop: 'var(--s-2)' }}>
        {sorted.map((d) => editing === d.id ? (
          <li key={d.id}><DayForm init={d} start={start} end={end} onCancel={() => setEditing(null)}
            onSave={(x) => run(() => updateDay(d.id, x, who))()}
            onRemove={run(() => removeDay(d.id))} /></li>
        ) : (
          <li key={d.id} className="work__row">
            <div className="work__main">
              <div style={{ fontWeight: 650 }}>{fmt(d.date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                {d.closed && <span className="pill" style={{ marginLeft: 8 }}>Closed</span>}</div>
              <div className="work__sub">{buildingsLabel(d.buildings, SCOPE_LABELS)}{d.cap ? ` · cap ${d.cap}` : ''}{d.note ? ` · ${d.note}` : ''}</div>
            </div>
            <button className="opt__icon" aria-label={`Edit ${d.date}`} onClick={() => setEditing(d.id)}><Pencil size={15} aria-hidden="true" /></button>
          </li>
        ))}
      </ul>
      {editing === 'new'
        ? <DayForm init={BLANK_DAY} start={start} end={end} onCancel={() => setEditing(null)} onSave={(x) => run(() => addDay(yearId, x, who))()} />
        : <button className="plan__add" style={{ marginTop: 'var(--s-3)' }} onClick={() => setEditing('new')}>+ Add a flex day</button>}
    </section>
  )
}

function DayForm({ init, start, end, onSave, onCancel, onRemove }: {
  init: DayInput; start: string; end: string
  onSave: (d: DayInput) => void; onCancel: () => void; onRemove?: () => void
}) {
  const [d, setD] = useState<DayInput>({ date: init.date, buildings: init.buildings, cap: init.cap, note: init.note, closed: init.closed })
  const every = d.buildings.includes('district')
  const toggle = (b: Scope) => setD((x) => {
    const cur: Scope[] = x.buildings.filter((s) => s !== 'district')
    const next = cur.includes(b) ? cur.filter((s) => s !== b) : [...cur, b]
    return { ...x, buildings: next.length ? next : ['district'] }
  })
  const outside = !!d.date && (d.date < start || d.date > end)
  return (
    <div className="host__card" style={{ marginTop: 'var(--s-2)' }}>
      <div className="host__grid">
        <Text label="Date" type="date" value={d.date} onChange={(v) => setD((x) => ({ ...x, date: v }))} required />
        <Text label="Cap (optional)" type="number" min={1} value={d.cap?.toString() ?? ''} placeholder="No limit"
          onChange={(v) => setD((x) => ({ ...x, cap: v.trim() ? Math.max(1, Math.floor(Number(v))) : null }))} />
      </div>
      {outside && <p className="opt__hint" style={{ marginTop: '-8px' }}>That date is outside this school year ({start} to {end}).</p>}
      <div className="opt__label" style={{ margin: 0 }}>Buildings</div>
      <div className="chips" style={{ margin: '4px 0 var(--s-3)' }}>
        <button type="button" aria-pressed={every} className={`chip${every ? ' is-on' : ''}`} onClick={() => setD((x) => ({ ...x, buildings: ['district'] }))}>Every building</button>
        {BUILDING_SCOPES.map((b) => {
          const on = !every && d.buildings.includes(b)
          return <button key={b} type="button" aria-pressed={on} className={`chip${on ? ' is-on' : ''}`} onClick={() => toggle(b)}>{SCOPE_LABELS[b]}</button>
        })}
      </div>
      <Text label="Note (optional)" value={d.note} onChange={(v) => setD((x) => ({ ...x, note: v }))} placeholder="e.g. Early out; afternoon only" />
      {init.date && (
        <label className="pick" style={{ marginBottom: 'var(--s-3)' }}>
          <input type="checkbox" checked={d.closed} onChange={(e) => setD((x) => ({ ...x, closed: e.target.checked }))} />
          <span className="pick__label">Closed to new requests (it stays on the calendar)</span>
        </label>
      )}
      <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        <button className="btn" style={{ width: 'auto' }} disabled={!d.date || outside} onClick={() => onSave(d)}>{init.date ? 'Save' : 'Add flex day'}</button>
        <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={onCancel}>Cancel</button>
        {onRemove && <button className="linkish" style={{ marginLeft: 'auto' }} onClick={onRemove}>Remove this date (entered by mistake)</button>}
      </div>
    </div>
  )
}

const LISTS: Array<{ k: keyof Pick<FpdConfig, 'focus' | 'places' | 'sendBack'>; title: string; hint: string }> = [
  { k: 'focus', title: 'Learning focus', hint: 'What teachers pick their PD is about. This is what the district reports on, so keep it to the goals you track.' },
  { k: 'places', title: 'Places', hint: 'Where PD happens. Teachers can also type an off-site place.' },
  { k: 'sendBack', title: 'Reasons a principal sends a request back', hint: 'Offered beside the principal’s own note.' },
]

function OptionLists() {
  const { profile } = useAuth()
  const cfg = useFpdConfig()
  const [draft, setDraft] = useState<FpdConfig | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  if (!cfg) return <Loading label="Loading the lists" />
  const c = draft ?? cfg
  const set = (k: (typeof LISTS)[number]['k'], list: FpdOption[]) => { setSaved(false); setDraft({ ...c, [k]: list }) }
  const save = async () => {
    setBusy(true); setErr(null)
    try { await saveConfig(c, profile?.displayName ?? ''); setDraft(null); setSaved(true) }
    catch (x) { setErr(x instanceof Error ? x.message : String(x)) } finally { setBusy(false) }
  }
  return (
    <section className="card" style={{ marginTop: 'var(--s-4)' }}>
      <h2 className="ov__h">Option lists</h2>
      <p className="ov__p">Retire an option instead of deleting it: requests that used it keep their words.</p>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      <div className="fpd__lists">
        {LISTS.map((l) => (
          <div key={l.k}>
            <div className="opt__label" style={{ margin: 0 }}>{l.title}</div>
            <p className="opt__hint" style={{ margin: '2px 0 var(--s-2)' }}>{l.hint}</p>
            <ul className="opt__list">
              {c[l.k].map((o, i) => (
                <li key={i} className="opt__row">
                  <input className="opt__input" value={o.name} aria-label={`${l.title} ${i + 1}`} style={o.retired ? { color: 'var(--ink-muted)', textDecoration: 'line-through' } : undefined}
                    onChange={(e) => set(l.k, c[l.k].map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                  <button className="linkish" style={{ whiteSpace: 'nowrap' }} onClick={() => set(l.k, c[l.k].map((x, j) => (j === i ? { ...x, retired: !x.retired } : x)))}>
                    {o.retired ? 'Bring back' : 'Retire'}
                  </button>
                </li>
              ))}
            </ul>
            <button className="plan__add" style={{ marginTop: 'var(--s-2)' }} onClick={() => set(l.k, [...c[l.k], { name: '' }])}>+ Add</button>
          </div>
        ))}
      </div>
      <div className="opt__bar" style={{ alignItems: 'center' }}>
        <button className="btn" style={{ width: 'auto' }} disabled={busy || !draft} onClick={save}>{busy ? 'Saving…' : 'Save the lists'}</button>
        {saved && !draft && <span className="work__sub" role="status">Saved.</span>}
      </div>
    </section>
  )
}

/** The count on the Flex PD tile and link: requests waiting on this principal. */
export function FlexPdBadge() {
  const n = useWaitingCount()
  return n > 0 ? <span className="work__badge" aria-label={`${n} waiting on you`}>{n}</span> : null
}
